<template>
  <section
    aria-label="Interactive campus map"
    class="a-cover"
    :class="{
      'md:pl-35vw':
        categoriesOpen && !isExploreAll && !isCampusIntro && !mediaOpen
    }"
  >
    <div
      id="map"
      ref="Map"
      class="rv-map__leaflet h-full w-full z-0"
      aria-label="Map with location"
    />

    <div
      v-if="showLatLngDisplay"
      class="absolute bottom-6 right-6 p-3 bg-white text-sm"
    >
      <span class="block">Latitude: {{ Math.round(dblClickCoords.x) }}</span>
      <span class="block">Longitude: {{ Math.round(dblClickCoords.y) }}</span>
    </div>
  </section>
</template>

<script>
/* global L: false */
import { mapState, mapGetters } from 'vuex'

import 'leaflet/dist/leaflet.css'
import 'leaflet/dist/leaflet.js'
import 'leaflet-rastercoords'

export default {
  name: 'Map',

  props: {
    category: {
      type: Object,
      default: () => {}
    },
    categories: {
      type: Array,
      default: () => {}
    },
    isExploreAll: {
      type: Boolean,
      default: true
    },
    isCampusIntro: {
      type: Boolean,
      default: true
    },
    mediaOpen: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      publicPath: process.env.BASE_URL,
      map: '',
      img: '',
      landmarkLayer: '',
      locationLayer: '',
      dblClickCoords: '',
      showLatLngDisplay: false
    }
  },

  computed: {
    ...mapState(['mobile', 'touch', 'categoriesOpen']),
    ...mapGetters(['locations', 'subjects']),

    landmarkLocations() {
      return this.locations.filter(
        ({ location_type }) => location_type === 'landmark_location'
      )
    },

    universityLocations() {
      if (this.category) {
        const locations = this.category.children.filter(
          ({ type }) => type === 'location'
        )

        const filterdChildren = locations.map(item => {
          let filtered

          if (!this.isExploreAll) {
            filtered = item.children.filter(
              o => o.parents.includes(this.category.id) || !o.parents
            )
          } else {
            filtered = item.children
          }

          return {
            ...item,
            children: filtered
          }
        })

        return filterdChildren.filter(item => item.children.length)
      }
      return this.locations
    }
  },

  watch: {
    category: {
      immediate: true,
      handler() {
        setTimeout(() => {
          this.addMapMarkers()
        }, 400)
      }
    },

    categoriesOpen: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    },

    isExploreAll: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    },

    isCampusIntro: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    },

    mediaOpen: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    }
  },

  mounted() {
    this.initMap()

    document.onkeyup = e => {
      if (e.ctrlKey && e.altKey && e.shiftKey && e.which === 85) {
        this.showLatLngDisplay = !this.showLatLngDisplay
      }
    }
  },

  methods: {
    initMap() {
      // Original image dimensions
      this.img = [8200, 5476]

      // Create the map
      this.map = L.map('map', {
        minZoom: 4,
        zoomControl: false,
        layerControl: false
      })

      // Assign map and image dimensions
      this.rc = new L.RasterCoords(this.map, this.img)
      // Set max zoom Level (might be `x` if gdal2tiles was called with `-z 0-x` option)
      this.map.setMaxZoom(this.rc.zoomLevel(5))
      // Set the view in the middle of the image
      this.map.setView(this.rc.unproject([this.img[0] / 2, this.img[1] / 2]), 3)
      // Define layer group for markers
      this.layerGroup = L.featureGroup([])
      // The tile layer containing the image generated with gdal2tiles --leaflet ...
      L.tileLayer(`${this.publicPath}uploads/tiles_8200x5476/{z}/{x}/{y}.png`, {
        noWrap: true
      }).addTo(this.map)

      // Add the markers
      this.addMapMarkers()
      // Just the utility to get coords
      this.map.on('dblclick', event => {
        this.dblClickCoords = this.rc.project(event.latlng)
        console.log(`Lat: ${Math.round(this.dblClickCoords.x)}`)
        console.log(`Lng: ${Math.round(this.dblClickCoords.y)}`)
      })
      // Beacuse the popup doesn't exist until clicked
      this.map.on('popupopen', this.bindPopupClick)
      this.map.on('popupclose', this.unbindPopupClick)
    },

    addMapMarkers() {
      if (this.locationLayer) {
        this.map.removeLayer(this.locationLayer)
      }

      this.locationLayer = L.featureGroup([]).addTo(this.map)

      this.universityLocations.map(item => {
        const img = {
          src: item?.image?.sizes?.thumbnail,
          alt: item?.image?.alt
        }

        let marker

        if (item.location_type === 'university_location') {
          const popupMarkup = `
            <div
              class="block min-h-24 w-72 cursor-pointer flex bg-white border border-body"
              data-slug="${item.slug}"
            >
              <div
                class="w-1/3 bg-cover bg-center flex flex-col flex-grow pointer-events-none flex-none"
                style="background-image: url(${img.src})"
              ></div>
              <div class="p-3 flex justify-between flex-col flex-grow pointer-events-none">
                <span class="block mb-2 text-base pointer-events-none">
                  ${this.htmlDecode(item.title)}
                </span>

                <span class="text-base text-primary pointer-events-none">
                  <span>Take a look around</span>
                  <img src="${require('@/assets/icon-arrow.svg')}" class="h-3 w-auto inline-block ml-1 -mt-px" />
                </span>
              </div>
            </div>
          `

          const icon =
            this?.category?.type === 'subject'
              ? 'subjects'
              : this.category
              ? this.category.icon
              : 'exploreall'

          const popup = new L.Popup({
            closeButton: false,
            offset: [0, -10]
          }).setContent(popupMarkup)

          marker = L.marker(
            this.rc.unproject([item.coordinates.lat, item.coordinates.lng]),
            {
              icon: L.icon({
                iconUrl: require(`@/assets/icon-${icon}.svg`),
                iconSize: [50, 50]
              }),
              keyboard: true
            }
          )
            .bindPopup(popup)
            .addTo(this.locationLayer)
        } else {
          const landmarkMarkup = `
            <div aria-label="${this.htmlDecode(item.title)}" class="${
            item.location_direction
          }-arrow relative group h-28 w-28 rounded-full focus-a text-base whitespace-no-wrap">
              <div class="relative h-full w-full rounded-full border-2 border-white">
                <img src="${img.src}" alt="${
            img.alt
          }" class="h-full w-full rounded-full" />
              </div>
            </div>
          `

          const landmarkDivIcon = L.divIcon({
            html: landmarkMarkup,
            iconSize: [100, 100]
          })

          const popup = new L.Popup({
            closeButton: false,
            className: 'landmark-popup'
          }).setContent('')

          marker = L.marker(
            this.rc.unproject([item.coordinates.lat, item.coordinates.lng]),
            {
              icon: landmarkDivIcon,
              keyboard: false
            }
          )
            .bindPopup(popup)
            .addTo(this.locationLayer)
        }

        const latLng = this.rc.project(marker.getLatLng())
        // const offsetLatLng = { x: latLng.x - 300, y: latLng.y }
        const offsetLatLng = { x: latLng.x, y: latLng.y }

        L.DomEvent.addListener(marker, 'click', () => {
          if (this.touch) {
            marker.openPopup()
          }

          if (item.location_type === 'landmark_location') {
            this.handleNavigation(item)
          }
        })

        const markerListLink = document.querySelector(
          `.map__marker-link--${item.slug}`
        )

        const markerListItem = L.DomUtil.create(
          'div',
          'map__marker-link',
          markerListLink
        )

        L.DomEvent.addListener(markerListItem, 'click', () => {
          marker.openPopup()

          this.map.setView(
            this.rc.unproject(offsetLatLng),
            this.map.getZoom(),
            { animate: true, pan: { duration: 1 } }
          )
        })

        marker.on('mouseover', () => {
          if (!this.touch) {
            // marker.openPopup()
          }
        })

        marker.on('mouseout', () => {
          if (!this.touch) {
            // marker.closePopup()
          }
        })

        marker.on('popupopen', () => {})
        marker.on('popupclose', () => {})
        L.DomEvent.addListener(markerListItem, 'mouseover', () => {})
        L.DomEvent.addListener(markerListItem, 'mouseout', () => {})

        // The marker domNode for handling focus outside of Leaflets built in methods
        const markerDomNode = marker.getElement()

        let mouseDown = false

        markerDomNode.addEventListener('mousedown', () => {
          mouseDown = true
        })

        markerDomNode.addEventListener('mouseup', () => {
          mouseDown = false
        })

        // Handle marker focus events
        markerDomNode.addEventListener('focus', e => {
          if (!this.touch && !mouseDown) {
            marker.openPopup()

            this.map.setView(
              this.rc.unproject(offsetLatLng),
              this.map.getZoom(),
              { animate: true, pan: { duration: 1 } }
            )
          }
          this.$announcer.set(this.htmlDecode(`Map location ${item.title}`))

          e.target.addEventListener('keyup', () => {
            if (event.keyCode === 27) {
              marker.closePopup()
            }
          })

          e.target.addEventListener('keyup', () => {
            if (event.keyCode === 13) {
              this.handleMarkerClick(item)
            }
          })
        })
      })

      // if (this.universityLocations.length) {
      //   this.map.fitBounds(
      //     this.locationLayer.getBounds(),
      //     { animate: true, duration: 0.5 },
      //     { padding: [50, 50] }
      //   )
      // }
    },

    handleNavigation(item) {
      if (this.category) {
        this.$router
          .push({
            name: 'campus-panorama',
            params: {
              category_id: this.category.slug,
              media_id: item.slug,
              panorama_id: item.children[0].slug
            }
          })
          .catch(() => {})
      } else {
        this.$router
          .push({
            name: 'campus-panorama',
            params: {
              category_id: 'explore-all',
              media_id: item.slug,
              panorama_id: item.children[0].slug
            }
          })
          .catch(() => {})
      }
    },

    bindPopupClick(e) {
      if (e.popup) {
        e.popup._wrapper.addEventListener('click', this.handlePopupClick)
      }
    },

    unbindPopupClick(e) {
      if (e.popup) {
        e.popup._wrapper.removeEventListener('click', this.handlePopupClick)
      }
    },

    handlePopupClick(e) {
      const location = this.universityLocations.find(
        ({ slug }) => slug === e.target.dataset.slug
      )

      this.handleNavigation(location)
    }
  }
}
</script>

<style>
#no-touch .rv-map__leaflet:focus:after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  pointer-events: none;
  user-select: none;
  border: 3px solid var(--color-focus);
  z-index: 500;
}

.leaflet-container {
  /* background-color:rgba(187,226,207,1); */
  /* background-color: #e3e6db; */
}

.leaflet-control-attribution.leaflet-control {
  display: none;
}

.leaflet-marker-icon: {
  border-radius: 1000px;
}

.leaflet-marker-icon:focus {
  outline: none;
  border: 3px solid var(--color-focus);
}

.leaflet-popup-tip {
  background: var(--color-body);
}

.leaflet-popup-content {
  /* background: transparent; */
  margin: 0;
}

.leaflet-popup-content-wrapper {
  background: transparent;
  border-radius: 0;
  padding: 0;
}

.leaflet-div-icon {
  border-radius: 1000px;
  background: transparent;
  border: none;
}

.leaflet-div-icon:focus {
  /* border: none; */
}

.landmark-popup .leaflet-popup-tip {
  background: transparent;
}

.map__marker-link {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.bottom-arrow:before {
  content: '';
  position: absolute;
  top: 99%;
  left: 50%;
  margin-left: -10px;
  border-width: 10px;
  border-style: solid;
  border-color: white transparent transparent transparent;
}

.top-arrow:before {
  content: '';
  position: absolute;
  bottom: 98%;
  left: 50%;
  margin-left: -10px;
  border-width: 10px;
  border-style: solid;
  border-color: transparent transparent white transparent;
}

.right-arrow:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 98%;
  margin-top: -10px;
  border-width: 10px;
  border-style: solid;
  border-color: transparent transparent transparent white;
}

.left-arrow:before {
  content: '';
  position: absolute;
  top: 50%;
  right: 98%;
  margin-top: -10px;
  border-width: 10px;
  border-style: solid;
  border-color: transparent white transparent transparent;
}
</style>
